import React, {ReactNode, useEffect, useRef, useState} from 'react';

import {v4} from 'uuid';

import StateButton from 'app/general/widgets/StateButton';
import {
  useAccountSettingsState,
  useUpdateAccountSettings,
} from 'app/settings/api';
import {gt} from 'app/utils/i18n';

import {MessageExchange} from './TextEdition/MessagePanel';

import {useWsOpenaiMessage} from '../hooks';
import {AI_SUGGESTION_PROMPT, AI_SUGGESTION_PROMPT_DEFAULT} from '../utils';

import type {SuggestionItem} from '../types';

type Props = {
  setResults: (results: Array<SuggestionItem>) => void;
  children: ReactNode;
  contentType?: string;
  initialThreadId?: string;
  disabled?: boolean;
  shouldUpdateAccount?: boolean;
};

const exampleIndustries = [
  'Sustainable Handmade Jewellery Store',
  'Pet Subscription Box Service',
  'Vintage Vinyl Record Shop',
  'Local Organic Produce Delivery',
];
const industryProcedures = {
  'Sustainable Handmade Jewellery Store': [
    'Design and Craftsmanship Workflow',
    'Jewellery Polishing and Finishing',
    'Packaging and Presentation Standards',
  ],
  'Pet Subscription Box Service': [
    'Monthly Box Assembly',
    'Shipping and Delivery Coordination',
    'Inventory Management for Subscription Cycles',
  ],
  'Vintage Vinyl Record Shop': [
    'Record Grading and Inspection',
    'Vinyl Cleaning and Maintenance',
    'Order Fulfillment Workflow',
  ],
  'Local Organic Produce Delivery': [
    'Produce Sourcing and Supplier Verification',
    'Weekly Packing and Quality Control',
    'Delivery Routing and Logistics Coordination',
  ],
};

const industryPolicies = {
  'Sustainable Handmade Jewellery Store': [
    'Ethical Sourcing Standards',
    'Returns and Repairs Terms',
    'Sustainability and Environmental Commitment',
  ],
  'Pet Subscription Box Service': [
    'Pet-Safe Product Standards',
    'Subscription Cancellation and Refund Terms',
    'Customer Data Privacy',
  ],
  'Vintage Vinyl Record Shop': [
    'Authenticity and Grading Criteria',
    'Return and Exchange Terms for Rare Items',
    'Environmental Commitment',
  ],
  'Local Organic Produce Delivery': [
    'Organic Certification and Supplier Standards',
    'Refund and Replacement for Perishables',
    'Customer Subscription Management',
  ],
};

function randomIndustry() {
  return exampleIndustries[
    Math.floor(Math.random() * exampleIndustries.length)
  ];
}

export const SuggestionChat = ({
  setResults,
  children,
  initialThreadId,
  shouldUpdateAccount = false,
  contentType = 'policies and procedures',
  disabled = false,
}: Props) => {
  const [threadId] = useState(initialThreadId || v4());

  const {account_id, industry_classification} = useAccountSettingsState().data!;

  const [suggestions, setSuggestions] = useState<Array<SuggestionItem>>([]);

  const {sendMessage, isLoading, commands, isError, isOpen} =
    useWsOpenaiMessage({
      type: 'document_suggestions',
      thread_id: threadId,
    });
  // The number of completed suggestions so far.
  // Stored in a ref since closures need to interact with it.
  const numCompletedSuggestions = useRef(0);

  useEffect(() => {
    if (suggestions.length > 0) {
      setResults(suggestions);
    }
  }, [suggestions]);

  const [category, setCategory] = useState(
    industry_classification || randomIndustry()
  );
  const [updateAccountSettings] = useUpdateAccountSettings();
  const createSuggestions = () => {
    if (shouldUpdateAccount) {
      updateAccountSettings({account_id, industry_classification: category});
    }
    const prompt =
      AI_SUGGESTION_PROMPT[contentType] || AI_SUGGESTION_PROMPT_DEFAULT;
    sendMessage(prompt.replace('{industry}', category));
  };

  useEffect(() => {
    if (!isLoading) {
      numCompletedSuggestions.current = suggestions.length;
    }
  }, [isLoading]);

  useEffect(() => {
    setResults([
      ...(industryProcedures[category] ?? []).map((name) => ({
        name,
        content_type: 'procedure',
        command: 'actions',
      })),
      ...(industryPolicies[category] ?? []).map((name) => ({
        name,
        content_type: 'policy',
        command: 'actions',
      })),
    ]);
  }, []);

  useEffect(() => {
    if (commands.length > 0) {
      const existing = suggestions
        .slice(0, numCompletedSuggestions.current)
        .map((suggestion) => suggestion.name);

      const addedSuggestions: Array<SuggestionItem> = [];
      commands.forEach((command) => {
        switch (command.command) {
          case 'actions':
            if (
              'name' in command &&
              'content_type' in command &&
              !existing.includes(command.name)
            ) {
              // suggestion type has name/content_type fields.
              addedSuggestions.push(command);
            }
            break;
        }
      });
      if (addedSuggestions.length > 0) {
        setSuggestions([
          ...suggestions.slice(0, numCompletedSuggestions.current),
          ...addedSuggestions,
        ]);
      }
    }
  }, [commands]);

  const initialAIMessage = gt(
    'Here are some suggested document names that could enhance your business operations in the "%(industry)s" industry.',
    {industry: category}
  );
  return (
    <>
      <div className="form-inline mt-4 mb-1">
        <label htmlFor="category" className="mr-2">
          {gt('Suggest %s for a business in', contentType)}
        </label>
        <input
          id="category"
          className="mr-2"
          type="text"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        />
        <StateButton
          busyText={gt('Suggesting...')}
          className="btn btn btn-primary"
          disabled={isError || !isOpen}
          onClick={createSuggestions}
        >
          {gt('Suggest')}
        </StateButton>{' '}
      </div>
      <div className="row">
        <div className="col-12 col-md-8">{children}</div>
        <div className="col-12 col-md-4">
          <MessageExchange
            initialAIMessage={initialAIMessage}
            threadId={threadId}
            chatType={'document_suggestions'}
            disabled={disabled}
          />
        </div>
      </div>
    </>
  );
};
